"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const clsx_1 = require("clsx");
const debounce_1 = require("../../utils/debounce");
const PropTypes = __importStar(require("prop-types"));
const React = __importStar(require("react"));
const validators_1 = require("../../core/validators/validators");
const interfaces_1 = require("./interfaces");
const ValidationPropTypes = {
    validator: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.arrayOf(PropTypes.func),
    ]),
    errorMessage: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.objectOf(PropTypes.string),
    ]),
    onValidationChanged: PropTypes.func,
    validateOnInit: PropTypes.string,
};
class Validation extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            errors: [],
            errorsChildMap: null,
            isValid: null,
            initialValue: null,
            lastValue: null,
            lastAction: null,
        };
        this.debouncedUpdateState = (0, debounce_1.debounce)(this.updateState);
    }
    componentDidMount() {
        if (this.props.validateOnInit || this.props.errors) {
            this.updateState(this.props.validateOnInit);
        }
    }
    componentDidUpdate(prevProps, prevState) {
        // call updateState whenever value or errorsChildMap change
        if (this.state.lastAction !== interfaces_1.ACTION.RESET &&
            this.state.lastAction !== interfaces_1.ACTION.ON_INIT &&
            (prevState.lastValue !== this.state.lastValue ||
                prevState.errorsChildMap !== this.state.errorsChildMap)) {
            this.debouncedUpdateState(this.state.lastValue);
        }
    }
    /**
     * Return the child component.
     * The onChange and onBlur methods are wrapped to call the validate method
     * @param child A React Element
     */
    getChildWithValidation(child) {
        if (!React.isValidElement(child)) {
            console.error('Child is not a valid React element', child);
        }
        return React.cloneElement(child, {
            onInit: (value) => {
                this.setState({
                    initialValue: value,
                    lastValue: value,
                    lastAction: interfaces_1.ACTION.ON_INIT,
                });
                if (child.props.onInit) {
                    child.props.onInit(value);
                }
            },
            onChange: (event) => {
                this.setState({
                    lastValue: event.target.value,
                    lastAction: interfaces_1.ACTION.ON_CHANGE,
                });
                if (child.props.onChange) {
                    child.props.onChange(event);
                }
            },
            onBlur: async (event) => {
                // on blur should not change the value 
                this.debouncedUpdateState(this.state.lastValue);
                if (child.props.onBlur) {
                    child.props.onBlur(event);
                }
            },
            onValidationChanged: (errorsChildMap) => {
                this.setState({ errorsChildMap });
            },
        });
    }
    async updateState(value) {
        let errorsMap = {};
        let valid = true;
        let errors = [];
        let validationResults;
        if (this.props.validator) {
            if (this.props.validator instanceof Array) {
                validationResults = await Promise.all(this.props.validator.map((validator) => validator(value)));
                errorsMap = validationResults.reduce((prev, curr) => (Object.assign(Object.assign({}, prev), curr)), {});
            }
            else {
                errorsMap = await this.props.validator(value);
            }
        }
        const isEmpty = Object.keys(errorsMap).length === 0;
        valid = (!errorsMap || isEmpty) && !this.state.errorsChildMap;
        if (this.props.onValidationChanged && valid !== this.state.isValid) {
            this.props.onValidationChanged(valid, errorsMap);
        }
        // compute props errors
        if (!valid && this.props.errorMessage) {
            Object.entries(errorsMap).forEach(([errorId, errorVal]) => {
                if (errorVal) {
                    if (this.props.errorMessage instanceof Object) {
                        errors.push(this.props.errorMessage[errorId]);
                    }
                    else {
                        errors.push(this.props.errorMessage);
                    }
                }
            });
        }
        // compute child errors
        if (!valid && this.state.errorsChildMap) {
            Object.entries(this.state.errorsChildMap).forEach(([errorId, errorVal]) => {
                if (errorVal) {
                    if (this.props.errorMessage instanceof Object &&
                        this.props.errorMessage[errorId]) {
                        // get message error from props if exists in errorMessage
                        errors.push(this.props.errorMessage[errorId]);
                    }
                    else {
                        // otherwise display the default one
                        errors.push(errorVal);
                    }
                }
            });
        }
        if (this.props.errors) {
            errors = this.props.errors;
        }
        // don't change the value here otherwise there are risks we get into an infinite loop
        this.setState({ isValid: valid, errors });
        return errors;
    }
    /**
     * Reset to default value and reset errors
     */
    reset() {
        this.setState({
            lastValue: this.state.initialValue,
            lastAction: interfaces_1.ACTION.RESET,
            isValid: null,
            errors: [],
            errorsChildMap: null,
        });
    }
    /**
     * Force validation to refresh, and return isValid state when triggered (used in Elements form before submission)
     */
    async refreshValidation() {
        await this.updateState(this.state.lastValue);
        return this.state.isValid;
    }
    render() {
        var _a;
        const { children, validator } = this.props;
        let childWithValidation;
        if (React.Children.count(children) === 0) {
            console.error('The Validation component requires one child component.');
        }
        else if (React.Children.count(children) > 1) {
            console.error(`The Validation component can wrap only one component. Found: ${React.Children.count(children)}`, children);
        }
        else {
            const child = React.Children.only(children);
            childWithValidation = this.getChildWithValidation(child);
        }
        const size = (_a = childWithValidation === null || childWithValidation === void 0 ? void 0 : childWithValidation.props) === null || _a === void 0 ? void 0 : _a['size']; // Experimental - Put itself automatically in small variant if the child is small
        const hasErrors = this.state.errors && this.state.errors.length;
        // aria-label to true if Validation contains a Required
        const ariaRequired = validator instanceof Array ? !!validator.find(v => v === validators_1.Validators.Required) : validator === validators_1.Validators.Required;
        return ((0, jsx_runtime_1.jsxs)("span", { className: (0, clsx_1.clsx)('tk-validation', {
                'tk-validation--error': hasErrors,
                [`tk-validation--${size}`]: size,
            }), "aria-required": ariaRequired, children: [childWithValidation, hasErrors ? ((0, jsx_runtime_1.jsx)("ul", { className: "tk-validation__errors", children: this.state.errors.map((error, index) => ((0, jsx_runtime_1.jsx)("li", { "aria-errormessage": error, children: error }, index))) })) : null] }));
    }
}
Validation.propTypes = ValidationPropTypes;
exports.default = Validation;
